import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Send, RefreshCw, Eye, MessageCircle, Mail, 
  Phone, Trash2, FileText, Upload, X, Loader2,
  Link as LinkIcon, Clock, CheckCircle, AlertTriangle,
  User, Plane, Briefcase, History, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

const ProspectsPage = () => {
  const [activeTab, setActiveTab] = useState('links'); // 'links' or 'profiles'
  const [links, setLinks] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSendFileModal, setShowSendFileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [expandedProfile, setExpandedProfile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [linksRes, profilesRes] = await Promise.all([
        api.get('/admin/prospect-links'),
        api.get('/admin/prospect-profiles')
      ]);
      setLinks(linksRes.data);
      setProfiles(profilesRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'En attente', icon: Clock },
      completed: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Rempli', icon: CheckCircle },
      expired: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Expiré', icon: AlertTriangle },
      new: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Nouveau', icon: User }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysLeft = (expiresAt) => {
    const now = new Date();
    const exp = new Date(expiresAt);
    const diff = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 'Expiré';
    if (diff === 1) return '1 jour';
    return `${diff} jours`;
  };

  // Create Link Modal
  const CreateLinkModal = () => {
    const [formData, setFormData] = useState({
      prospect_name: '',
      contact: '',
      send_via: 'whatsapp'
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.prospect_name || !formData.contact) {
        toast.error('Veuillez remplir tous les champs');
        return;
      }

      setSubmitting(true);
      try {
        await api.post('/admin/prospect-links', formData);
        toast.success('Lien créé et envoyé !');
        setShowCreateModal(false);
        fetchData();
      } catch (err) {
        toast.error('Erreur lors de la création');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#0F172A] rounded-2xl border border-white/10 w-full max-w-md overflow-hidden"
        >
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Créer un lien de profilage</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-[#64748B] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="text-sm text-white font-medium">Nom du prospect *</label>
              <input
                type="text"
                value={formData.prospect_name}
                onChange={(e) => setFormData({ ...formData, prospect_name: e.target.value })}
                placeholder="Jean Dupont"
                className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37]"
              />
            </div>

            <div>
              <label className="text-sm text-white font-medium">Mode d'envoi *</label>
              <div className="flex gap-3 mt-2">
                <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                  formData.send_via === 'whatsapp' ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-white/10 bg-white/5 text-[#94A3B8]'
                }`}>
                  <input
                    type="radio"
                    checked={formData.send_via === 'whatsapp'}
                    onChange={() => setFormData({ ...formData, send_via: 'whatsapp' })}
                    className="sr-only"
                  />
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </label>
                <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                  formData.send_via === 'email' ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-white/10 bg-white/5 text-[#94A3B8]'
                }`}>
                  <input
                    type="radio"
                    checked={formData.send_via === 'email'}
                    onChange={() => setFormData({ ...formData, send_via: 'email' })}
                    className="sr-only"
                  />
                  <Mail className="w-4 h-4" />
                  Email
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm text-white font-medium">
                {formData.send_via === 'whatsapp' ? 'Numéro WhatsApp *' : 'Adresse Email *'}
              </label>
              <input
                type={formData.send_via === 'email' ? 'email' : 'tel'}
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                placeholder={formData.send_via === 'whatsapp' ? '+243 XXX XXX XXX' : 'email@exemple.com'}
                className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37]"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 btn-glass">
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 btn-gold">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer le lien
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  };

  // Profile Detail Modal
  const ProfileDetailModal = () => {
    if (!selectedProfile) return null;
    const fd = selectedProfile.form_data;

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#0F172A] rounded-2xl border border-white/10 w-full max-w-2xl my-8"
        >
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Profil de {fd.full_name}</h2>
              <button onClick={() => setShowProfileModal(false)} className="text-[#64748B] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Identity */}
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="font-semibold text-white">Identité</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#64748B]">Nom:</span> <span className="text-white">{fd.full_name}</span></div>
                <div><span className="text-[#64748B]">Email:</span> <span className="text-white">{fd.email}</span></div>
                <div><span className="text-[#64748B]">Téléphone:</span> <span className="text-white">{fd.phone}</span></div>
                <div><span className="text-[#64748B]">Passeport:</span> <span className={fd.has_passport ? 'text-green-400' : 'text-red-400'}>{fd.has_passport ? 'Oui' : 'Non'}</span></div>
              </div>
            </div>

            {/* Travel */}
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Plane className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="font-semibold text-white">Projet de Voyage</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#64748B]">Objet:</span> <span className="text-white">{fd.travel_purpose}</span></div>
                <div><span className="text-[#64748B]">Destination:</span> <span className="text-white">{fd.destination}</span></div>
              </div>
            </div>

            {/* Socio-Professional (if Visite) */}
            {fd.travel_purpose === 'Visite' && (
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-4 h-4 text-[#D4AF37]" />
                  <h3 className="font-semibold text-white">Profil Socio-Professionnel</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-[#64748B]">Profession:</span> <span className="text-white">{fd.profession}</span></div>
                  <div><span className="text-[#64748B]">Situation:</span> <span className="text-white">{fd.marital_status}</span></div>
                  <div><span className="text-[#64748B]">Enfants:</span> <span className={fd.has_children ? 'text-green-400' : 'text-[#94A3B8]'}>{fd.has_children ? 'Oui' : 'Non'}</span></div>
                  <div><span className="text-[#64748B]">Bien immobilier:</span> <span className={fd.owns_property ? 'text-green-400' : 'text-[#94A3B8]'}>{fd.owns_property ? 'Oui' : 'Non'}</span></div>
                  <div className="col-span-2"><span className="text-[#64748B]">Compte bancaire actif:</span> <span className={fd.has_bank_account ? 'text-green-400' : 'text-[#94A3B8]'}>{fd.has_bank_account ? 'Oui' : 'Non'}</span></div>
                </div>
              </div>
            )}

            {/* Travel History */}
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="font-semibold text-white">Historique de Voyage</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#64748B]">Visa (5 ans):</span> <span className={fd.had_visa_last_5_years ? 'text-green-400' : 'text-[#94A3B8]'}>{fd.had_visa_last_5_years ? 'Oui' : 'Non'}</span></div>
                <div><span className="text-[#64748B]">Visa refusé:</span> <span className={fd.visa_refused ? 'text-red-400' : 'text-green-400'}>{fd.visa_refused ? 'Oui' : 'Non'}</span></div>
              </div>
              {fd.additional_message && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-[#64748B] text-xs mb-1">Message:</p>
                  <p className="text-white text-sm">{fd.additional_message}</p>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-white/10 flex gap-3">
            <Button onClick={() => { setShowProfileModal(false); setShowContactModal(true); }} className="flex-1 btn-gold">
              <MessageCircle className="w-4 h-4 mr-2" />
              Contacter
            </Button>
            <Button onClick={() => { setShowProfileModal(false); setShowSendFileModal(true); }} className="flex-1 btn-glass text-blue-400">
              <Upload className="w-4 h-4 mr-2" />
              Envoyer fichier
            </Button>
            <Button onClick={() => setShowProfileModal(false)} className="btn-glass">
              Fermer
            </Button>
          </div>
        </motion.div>
      </div>
    );
  };

  // Contact Modal
  const ContactModal = () => {
    const [contactVia, setContactVia] = useState('whatsapp');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    if (!selectedProfile) return null;

    const handleSend = async () => {
      if (!message.trim()) {
        toast.error('Veuillez saisir un message');
        return;
      }

      setSending(true);
      try {
        await api.post(`/admin/prospect-profiles/${selectedProfile.id}/contact?message=${encodeURIComponent(message)}&via=${contactVia}`);
        toast.success('Message envoyé !');
        setShowContactModal(false);
        setMessage('');
        fetchData();
      } catch (err) {
        toast.error('Erreur lors de l\'envoi');
      } finally {
        setSending(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#0F172A] rounded-2xl border border-white/10 w-full max-w-md"
        >
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Contacter {selectedProfile.form_data.full_name}</h2>
              <button onClick={() => setShowContactModal(false)} className="text-[#64748B] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm text-white font-medium">Mode de contact</label>
              <div className="flex gap-3 mt-2">
                <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border cursor-pointer ${
                  contactVia === 'whatsapp' ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-white/10 bg-white/5 text-[#94A3B8]'
                }`}>
                  <input type="radio" checked={contactVia === 'whatsapp'} onChange={() => setContactVia('whatsapp')} className="sr-only" />
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </label>
                <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border cursor-pointer ${
                  contactVia === 'email' ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-white/10 bg-white/5 text-[#94A3B8]'
                }`}>
                  <input type="radio" checked={contactVia === 'email'} onChange={() => setContactVia('email')} className="sr-only" />
                  <Mail className="w-4 h-4" />
                  Email
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm text-white font-medium">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Votre message..."
                rows={5}
                className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37] resize-none"
              />
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setShowContactModal(false)} className="flex-1 btn-glass">
                Annuler
              </Button>
              <Button onClick={handleSend} disabled={sending} className="flex-1 btn-gold">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // Send File Modal
  const SendFileModal = () => {
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState('Veuillez trouver ci-joint le document demandé.');
    const [sending, setSending] = useState(false);

    if (!selectedProfile) return null;

    const handleFileChange = (e) => {
      if (e.target.files && e.target.files[0]) {
        setFile(e.target.files[0]);
      }
    };

    const handleSend = async () => {
      if (!file) {
        toast.error('Veuillez sélectionner un fichier');
        return;
      }

      setSending(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await api.post(
          `/admin/prospect-profiles/${selectedProfile.id}/send-file?message=${encodeURIComponent(message)}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success('Fichier envoyé par email !');
        setShowSendFileModal(false);
        setFile(null);
        setMessage('Veuillez trouver ci-joint le document demandé.');
        fetchData();
      } catch (err) {
        toast.error('Erreur lors de l\'envoi du fichier');
      } finally {
        setSending(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#0F172A] rounded-2xl border border-white/10 w-full max-w-md"
        >
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Envoyer un fichier</h2>
              <button onClick={() => setShowSendFileModal(false)} className="text-[#64748B] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[#64748B] text-sm mt-1">Vers: {selectedProfile.form_data.full_name} ({selectedProfile.form_data.email})</p>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm text-white font-medium">Fichier *</label>
              <div className="mt-2">
                <label className="flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="sr-only"
                    data-testid="file-input"
                  />
                  {file ? (
                    <div className="flex items-center gap-2 text-[#D4AF37]">
                      <FileText className="w-5 h-5" />
                      <span className="text-sm">{file.name}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-[#64748B]">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">Cliquez pour sélectionner un fichier</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm text-white font-medium">Message (optionnel)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Message accompagnant le fichier..."
                rows={3}
                className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37] resize-none"
              />
            </div>

            <p className="text-xs text-[#64748B] flex items-center gap-1">
              <Mail className="w-3 h-3" />
              Le fichier sera envoyé par email à {selectedProfile.form_data.email}
            </p>

            <div className="flex gap-3">
              <Button onClick={() => setShowSendFileModal(false)} className="flex-1 btn-glass">
                Annuler
              </Button>
              <Button onClick={handleSend} disabled={sending || !file} className="flex-1 btn-gold">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // Resend link handler
  const handleResend = async (linkId) => {
    try {
      await api.post(`/admin/prospect-links/${linkId}/resend`);
      toast.success('Lien renvoyé !');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors du renvoi');
    }
  };

  // Delete link handler
  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Supprimer ce lien ?')) return;
    try {
      await api.delete(`/admin/prospect-links/${linkId}`);
      toast.success('Lien supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Delete profile handler
  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Supprimer ce profil ?')) return;
    try {
      await api.delete(`/admin/prospect-profiles/${profileId}`);
      toast.success('Profil supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Prospects</h1>
          <p className="text-[#94A3B8] text-sm">Gérez vos liens de profilage et les profils soumis</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="btn-gold" data-testid="create-prospect-link">
          <Plus className="w-4 h-4 mr-2" />
          Créer un lien
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-white/5 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('links')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'links' ? 'bg-[#D4AF37] text-black' : 'text-[#94A3B8] hover:text-white'
          }`}
        >
          <LinkIcon className="w-4 h-4 inline mr-2" />
          Liens ({links.length})
        </button>
        <button
          onClick={() => setActiveTab('profiles')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'profiles' ? 'bg-[#D4AF37] text-black' : 'text-[#94A3B8] hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Profils ({profiles.length})
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      ) : (
        <>
          {/* Links Tab */}
          {activeTab === 'links' && (
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
              {links.length === 0 ? (
                <div className="text-center py-12">
                  <LinkIcon className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">Aucun lien créé</p>
                  <Button onClick={() => setShowCreateModal(true)} className="btn-gold mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer le premier lien
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5 border-b border-white/10">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#64748B] uppercase">Prospect</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#64748B] uppercase">Contact</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#64748B] uppercase">Envoi</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#64748B] uppercase">Statut</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#64748B] uppercase">Expiration</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-[#64748B] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {links.map((link) => (
                        <tr key={link.id} className="hover:bg-white/5">
                          <td className="px-4 py-3">
                            <p className="text-white font-medium">{link.prospect_name}</p>
                            <p className="text-[#64748B] text-xs">{formatDate(link.created_at)}</p>
                          </td>
                          <td className="px-4 py-3 text-[#94A3B8] text-sm">{link.contact}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs ${link.send_via === 'whatsapp' ? 'text-green-400' : 'text-blue-400'}`}>
                              {link.send_via === 'whatsapp' ? <MessageCircle className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                              {link.send_via === 'whatsapp' ? 'WhatsApp' : 'Email'}
                            </span>
                          </td>
                          <td className="px-4 py-3">{getStatusBadge(link.status)}</td>
                          <td className="px-4 py-3 text-sm text-[#94A3B8]">{link.status !== 'completed' ? getDaysLeft(link.expires_at) : '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {link.status !== 'completed' && (
                                <button 
                                  onClick={() => handleResend(link.id)}
                                  className="p-2 text-[#64748B] hover:text-[#D4AF37] hover:bg-white/5 rounded-lg"
                                  title="Renvoyer"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </button>
                              )}
                              <button 
                                onClick={() => handleDeleteLink(link.id)}
                                className="p-2 text-[#64748B] hover:text-red-400 hover:bg-white/5 rounded-lg"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Profiles Tab */}
          {activeTab === 'profiles' && (
            <div className="space-y-4">
              {profiles.length === 0 ? (
                <div className="bg-white/5 rounded-xl border border-white/10 text-center py-12">
                  <FileText className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">Aucun profil soumis</p>
                </div>
              ) : (
                profiles.map((profile) => (
                  <motion.div
                    key={profile.id}
                    layout
                    className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
                  >
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                      onClick={() => setExpandedProfile(expandedProfile === profile.id ? null : profile.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{profile.form_data.full_name}</p>
                          <p className="text-[#64748B] text-sm">{profile.form_data.destination} • {profile.form_data.travel_purpose}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(profile.status)}
                        <span className="text-[#64748B] text-sm">{formatDate(profile.submitted_at)}</span>
                        {expandedProfile === profile.id ? (
                          <ChevronUp className="w-5 h-5 text-[#64748B]" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#64748B]" />
                        )}
                      </div>
                    </div>

                    <AnimatePresence>
                      {expandedProfile === profile.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-white/10"
                        >
                          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-[#64748B]">Email</p>
                              <p className="text-white">{profile.form_data.email}</p>
                            </div>
                            <div>
                              <p className="text-[#64748B]">Téléphone</p>
                              <p className="text-white">{profile.form_data.phone}</p>
                            </div>
                            <div>
                              <p className="text-[#64748B]">Passeport</p>
                              <p className={profile.form_data.has_passport ? 'text-green-400' : 'text-red-400'}>
                                {profile.form_data.has_passport ? 'Oui' : 'Non'}
                              </p>
                            </div>
                            <div>
                              <p className="text-[#64748B]">Visa refusé</p>
                              <p className={profile.form_data.visa_refused ? 'text-red-400' : 'text-green-400'}>
                                {profile.form_data.visa_refused ? 'Oui' : 'Non'}
                              </p>
                            </div>
                          </div>
                          <div className="p-4 pt-0 flex gap-2">
                            <Button 
                              onClick={() => { setSelectedProfile(profile); setShowProfileModal(true); }}
                              className="btn-glass text-sm"
                              data-testid={`view-profile-${profile.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Voir détails
                            </Button>
                            <Button 
                              onClick={() => { setSelectedProfile(profile); setShowContactModal(true); }}
                              className="btn-gold text-sm"
                              data-testid={`contact-profile-${profile.id}`}
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              Contacter
                            </Button>
                            <Button 
                              onClick={() => { setSelectedProfile(profile); setShowSendFileModal(true); }}
                              className="btn-glass text-sm text-blue-400 hover:bg-blue-500/10"
                              data-testid={`send-file-${profile.id}`}
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Fichier
                            </Button>
                            <Button 
                              onClick={() => handleDeleteProfile(profile.id)}
                              className="btn-glass text-sm text-red-400 hover:bg-red-500/10"
                              data-testid={`delete-profile-${profile.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showCreateModal && <CreateLinkModal />}
        {showProfileModal && <ProfileDetailModal />}
        {showContactModal && <ContactModal />}
        {showSendFileModal && <SendFileModal />}
      </AnimatePresence>
    </div>
  );
};

export default ProspectsPage;
